// import { DocumentDataType, OrderedDocument } from '@/types/doc';
// import { Icon } from '@iconify/react';
// import { Card, ScrollShadow, Spinner } from '@nextui-org/react';

// import DocumentViewerButton from '@/components/document-viewer/document-viewer-button';
// import { useOrderedDocuments } from '@/hooks/queries/ordered-documents';

// const LibraryPage = () => {
//   const { data: documents, isLoading: isLoadingDocuments } =
//     useOrderedDocuments();

//   return (
//     <div className="flex flex-col">
//       {/* Header Section with Back Button */}
//       <div className="mb-2 flex items-center ml-1">
//         {/* <Button variant="light" onClick={() => navigate(-1)} isIconOnly>
//           <Icon icon="lucide:chevron-left" className="h-5 w-5" />
//         </Button> */}
//         <h3 className="text-lg font-bold">Library</h3>
//       </div>

//       <div className="mx-auto w-full flex-1 overflow-hidden">
//         {/* Documents Grid with ScrollShadow */}
//         <ScrollShadow className="h-full">
//           <Card className="min-h-[70vh]" shadow="none">
//             <div className="space-y-4">
//               {isLoadingDocuments ? (
//                 <div className="flex justify-center items-center h-[400px]">
//                   <Spinner size="lg" />
//                 </div>
//               ) : documents?.results && documents.results.length > 0 ? (
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
//                   {documents.results.map((doc: OrderedDocument) => (
//                     <Card
//                       key={doc.id}
//                       className="p-4 border-1 hover:shadow-md transition-shadow"
//                       shadow="none"
//                     >
//                       <div className="flex justify-between items-center">
//                         <div className="flex items-center gap-3 flex-1 min-w-0">
//                           <Icon
//                             icon="mdi:file-document-outline"
//                             className="text-primary text-2xl flex-shrink-0"
//                           />
//                           <p
//                             className="text-sm font-medium truncate"
//                             title={doc.document.filename}
//                           >
//                             {doc.document.filename}
//                           </p>
//                         </div>
//                         <DocumentViewerButton
//                           document={doc.document as unknown as DocumentDataType}
//                           iconName="mdi:eye"
//                           buttonClassName="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
//                           className="text-gray-400 hover:text-primary text-xl"
//                         />
//                       </div>
//                     </Card>
//                   ))}
//                 </div>
//               ) : (
//                 <div className="flex flex-col items-center justify-center h-[400px] text-center">
//                   <Icon
//                     icon="mdi:file-document-multiple-outline"
//                     className="text-gray-300 text-6xl mb-4"
//                   />
//                   <h3 className="text-lg font-medium text-gray-600 mb-2">
//                     No documents available
//                   </h3>
//                   <p className="text-gray-500 text-sm">
//                     Documents will appear here when they are uploaded to your
//                     library
//                   </p>
//                 </div>
//               )}
//             </div>
//           </Card>
//         </ScrollShadow>
//       </div>
//     </div>
//   );
// };

// export default LibraryPage;

import DocumentViewerButton from '@/components/document-viewer/document-viewer-button';
import { useOrderedDocuments } from '@/hooks/queries/ordered-documents';
import { DocumentDataType, OrderedDocument } from '@/types/doc';
import { BookOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { BookOpenIcon as BookOpenIconSolid } from '@heroicons/react/24/solid';
import { Card, Input, ScrollShadow, Spinner } from '@nextui-org/react';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';

interface LibraryDocument extends OrderedDocument {
  // Extended properties for display
  category?: string;
  readTime?: string;
  rating?: number;
  type?: 'Article' | 'Guide' | 'Video' | 'Workbook';
}

// Mock data for enhanced display - in production, these would come from the API
const getDocumentMetadata = (
  doc: OrderedDocument,
  index: number
): LibraryDocument => {
  const categories = [
    'Fundamentals',
    'Advanced',
    'Leadership',
    'Communication',
  ];
  const types: ('Article' | 'Guide' | 'Video' | 'Workbook')[] = [
    'Article',
    'Guide',
    'Video',
    'Workbook',
  ];
  const readTimes = ['5 min read', '10 min read', '15 min read', '20 min read'];
  const ratings = [4.5, 4.7, 4.8, 4.9, 5.0];

  return {
    ...doc,
    category: categories[index % categories.length],
    readTime: readTimes[index % readTimes.length],
    rating: ratings[index % ratings.length],
    type: types[index % types.length],
  };
};

const LibraryPage = () => {
  const { data: documents, isLoading: isLoadingDocuments } =
    useOrderedDocuments();
  const [searchQuery, setSearchQuery] = useState('');

  const enhancedDocuments = useMemo(() => {
    if (!documents?.results) return [];
    return documents.results.map((doc, index) =>
      getDocumentMetadata(doc, index)
    );
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return enhancedDocuments;
    return enhancedDocuments.filter((doc) =>
      doc.document.filename.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [enhancedDocuments, searchQuery]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col pb-24 md:pb-8 pt-4 md:pt-0 p-2 sm:p-0"
    >
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Library</h1>
        <p className="text-gray-500 text-sm md:text-base">
          Access your leadership resources and guides
        </p>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <Input
          placeholder="Search library..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startContent={
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
          }
          classNames={{
            base: 'max-w-full',
            inputWrapper:
              'bg-gray-50 border-1 border-gray-200 hover:border-gray-300 focus-within:!border-primary rounded-xl h-12',
            input: 'text-base',
          }}
        />
      </motion.div>

      {/* Documents Grid */}
      <ScrollShadow className="flex-1" offset={10}>
        {isLoadingDocuments ? (
          <div className="flex justify-center items-center h-[400px]">
            <Spinner size="lg" />
          </div>
        ) : filteredDocuments.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1"
          >
            {filteredDocuments.map((doc) => (
              <motion.div key={doc.id} variants={itemVariants}>
                <Card
                  className="p-4 md:p-5 border-1 shadow-md hover:shadow-lg transition-all duration-200 bg-white rounded-2xl shadow-sm"
                  // shadow="none"
                >
                  <div className="flex gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-rose-50 flex items-center justify-center">
                        <BookOpenIconSolid className="w-5 h-5 md:w-5 md:h-5 text-rose-400" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex w-full  min-w-0 justify-between items-center">
                      <h3 className="text-base font-semibold text-gray-900 mb-0.5 line-clamp-1">
                        {doc.document.filename.replace(/\.[^/.]+$/, '')}
                      </h3>

                      {/* Read Time */}
                      {/* <div className="flex items-center gap-1 text-xs text-gray-400">
                          <ClockIcon className="w-3.5 h-3.5" />
                          <span>{doc.readTime}</span>
                        </div> */}

                      {/* Rating */}
                      {/* <div className="flex items-center gap-1 text-xs text-amber-500">
                          <StarIcon className="w-3.5 h-3.5 fill-current" />
                          <span>{doc.rating}</span>
                        </div> */}

                      {/* Type Badge - Now inline */}
                      {/* <span
                          className={`text-xs font-semibold px-3 py-1 rounded-md ${getTypeColor(doc.type || 'Article')}`}
                        >
                          {doc.type}
                        </span> */}
                      {/* </div> */}
                    </div>

                    {/* Action Button */}
                    <div className="flex-shrink-0 self-start mt-1">
                      <DocumentViewerButton
                        document={doc.document as unknown as DocumentDataType}
                        iconName="heroicons:eye"
                        buttonClassName="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        className="text-gray-300 hover:text-primary text-xl"
                      />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center h-[400px] text-center px-4"
          >
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <BookOpenIcon className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-lg font-medium text-gray-600 mb-2">
              {searchQuery ? 'No results found' : 'No documents available'}
            </h3>
            <p className="text-gray-400 text-sm max-w-xs">
              {searchQuery
                ? `No documents match "${searchQuery}"`
                : 'Documents will appear here when they are uploaded to your library'}
            </p>
          </motion.div>
        )}
      </ScrollShadow>
    </motion.div>
  );
};

export default LibraryPage;
